import React, { useState } from 'react';

const DEFAULT_INDUSTRIES = [
  'Technology',
  'Healthcare',
  'Finance',
  'Education',
  'Retail',
  'Manufacturing',
  'Construction',
  'Hospitality',
  'Other'
];

const DEFAULT_SCENARIOS = [
  'Invoice owing 10 days for $5335',
  'Late payment on $10,000 contract',
  'Unpaid services worth $2500',
  'Overdue account of $7500',
  'Outstanding balance of $5000 for 45 days'
];

const CBAEmployerForm = ({ howItWorksContent, demoFormSettings }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    companyName: '',
    mobileNumber: '',
    industry: '',
    scenario: '',
  });

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const parseScenarioSettings = (scenarioSettings) => {
    if (Array.isArray(scenarioSettings)) {
      return scenarioSettings;
    }

    if (typeof scenarioSettings === 'string' && scenarioSettings.trim()) {
      try {
        const parsedScenarios = JSON.parse(scenarioSettings);
        return Array.isArray(parsedScenarios) ? parsedScenarios : [scenarioSettings];
      } catch (error) {
        return scenarioSettings.split('\n');
      }
    }

    return [];
  };

  const configuredScenarios = parseScenarioSettings(demoFormSettings?.scenarios)
    .map((scenario) => (typeof scenario === 'string' ? scenario.trim() : ''))
    .filter(Boolean);

  const industries = DEFAULT_INDUSTRIES;
  const scenarios = Array.from(new Set([...configuredScenarios, ...DEFAULT_SCENARIOS]));
  const formTitle = demoFormSettings?.form_title || 'Try our Free Live Demo!';
  const primaryColor = demoFormSettings?.primary_color || '#0065FF';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const normalizeAustralianMobileNumber = (mobileNumber) => {
    const trimmedMobile = mobileNumber ? String(mobileNumber).trim() : '';
    if (!trimmedMobile) return '';

    const digitsOnly = trimmedMobile.replace(/\D/g, '');

    if (digitsOnly.startsWith('04') && digitsOnly.length === 10) {
      return `+61${digitsOnly.slice(1)}`;
    }

    if (digitsOnly.startsWith('614') && digitsOnly.length === 11) {
      return `+${digitsOnly}`;
    }

    return '';
  };

  const isValidAustralianMobileNumber = (mobileNumber) => Boolean(normalizeAustralianMobileNumber(mobileNumber));

  const validateForm = () => {
    if (!formData.firstName.trim()) return "First Name is required";
    if (!formData.companyName.trim()) return "Company Name is required";
    if (!formData.mobileNumber.trim()) return "Mobile Number is required";
    if (!isValidAustralianMobileNumber(formData.mobileNumber)) return "Enter a valid Australian mobile number, e.g. 0412 345 678 or +61 412 345 678";
    if (!formData.industry) return "Industry is required";
    if (!formData.scenario) return "Scenario is required";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError('');

    const payload = {
      first_name: formData.firstName.trim(),
      company_name: formData.companyName.trim(),
      mobile_number: normalizeAustralianMobileNumber(formData.mobileNumber),
      industry: formData.industry,
      scenario: formData.scenario,
    };

    try {
      const response = await fetch('https://ai.mploi.com/api/public-demo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(result.error || 'Verification failed or call could not be started');
      }

      setFormSubmitted(true);
      setError('');
    } catch (err) {
      console.log("ERROR IS", err);
      setError(err.message || "An error occurred setting up the call.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (formSubmitted) {
    return (
      <div className="verification-section">
        <h2>Call Initiated</h2>
        <p>Our AI Agent will now call you to give a demonstration.</p>
        <button onClick={() => window.location.href = '/signup?accounts=true'} className="border-0 px-4 py-2 primaryButton">
          Connect now
        </button>
      </div>
    );
  }

  const content = howItWorksContent || {
    title: 'How the Demo works...',
    line1: "Fill out the fields below and press 'Submit'",
    line2: 'Our simulator will call you',
    line3: 'Answer and interact with our simulator',
    line4: 'Experience our AI driven accounts first hand',
    line5: 'Start your demo now!'
  };

  return (
    <div className="employer-section">
      <h1 className="main-heading">{formTitle}</h1>
      <div className="verification-section">
        <h2>{content.title}</h2>
        <style>{`ol { padding-left: 20px; } ol li { margin-bottom: 10px; }`}</style>
        <ol>
          <li>{content.line1}</li>
          <li>{content.line2}</li>
          <li>{content.line3}</li>
          <li>{content.line4}</li>
        </ol>
        <p>{content.line5}</p>
      </div>
      <form onSubmit={handleSubmit} className="employer-form">
        <div className="form-group">
          <label htmlFor="firstName">Your First Name</label>
          <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} className="form-control" required />
        </div>
        <div className="form-group">
          <label htmlFor="companyName">Your Company Name</label>
          <input type="text" id="companyName" name="companyName" value={formData.companyName} onChange={handleChange} className="form-control" required />
        </div>
        <div className="form-group">
          <label htmlFor="mobileNumber">Mobile Number</label>
          <input
            type="tel"
            id="mobileNumber"
            name="mobileNumber"
            value={formData.mobileNumber}
            onChange={handleChange}
            className="form-control"
            placeholder="0412 345 678"
            inputMode="tel"
            autoComplete="tel"
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="industry">What is your industry?</label>
          <select id="industry" name="industry" value={formData.industry} onChange={handleChange} className="form-control" required>
            <option value="">Select an industry</option>
            {industries.map((industry, index) => <option key={index} value={industry}>{industry}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="scenario">Example scenario</label>
          <select id="scenario" name="scenario" value={formData.scenario} onChange={handleChange} className="form-control" required>
            <option value="">Select a scenario</option>
            {scenarios.map((scenario, index) => <option key={index} value={scenario}>{scenario}</option>)}
          </select>
        </div>
        {error && <div className="alert alert-danger">{error}</div>}
        <button
          type="submit"
          className="border-0 px-4 py-2 primaryButton"
          style={{ backgroundColor: primaryColor, borderColor: primaryColor }}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Setting up call...' : "I'm ready to receive a call"}
        </button>
      </form>
    </div>
  );
};

export default CBAEmployerForm;
